import { getSession } from '../session.js';
import { showToast } from './Toast.js';
import type { Order } from '../../core/app-store.js';

type NavigateFn = (route: string, params?: Record<string, string>) => void;

let pollInterval: ReturnType<typeof setInterval> | null = null;

export async function renderKitchenDisplay(container: HTMLElement, navigate: NavigateFn): Promise<void> {
  if (pollInterval) { clearInterval(pollInterval); pollInterval = null; }

  const session = getSession();

  container.innerHTML = `
    <div class="kitchen-wrap">
      <div class="kitchen-header">
        <div style="display:flex;align-items:center;gap:12px">
          <button class="btn btn-ghost btn-sm" id="back-btn" style="color:#fff;border-color:rgba(255,255,255,.3)">← Back</button>
          <div>
            <div style="font-size:20px;font-weight:800">🍳 Kitchen Display</div>
            <div style="font-size:11px;opacity:.7" id="last-updated">Updating…</div>
          </div>
        </div>
        <div style="display:flex;gap:8px;align-items:center">
          <div class="kitchen-legend">
            <span style="background:#f59e0b;width:10px;height:10px;border-radius:50%;display:inline-block"></span> New
            <span style="background:#3b82f6;width:10px;height:10px;border-radius:50%;display:inline-block;margin-left:8px"></span> Preparing
            <span style="background:#10b981;width:10px;height:10px;border-radius:50%;display:inline-block;margin-left:8px"></span> Ready
          </div>
          <button class="btn btn-ghost btn-sm" id="history-btn" style="color:#fff;border-color:rgba(255,255,255,.3)">Order History</button>
        </div>
      </div>
      <div class="kitchen-board" id="kitchen-board">
        <div style="color:rgba(255,255,255,.5);text-align:center;padding:60px;font-size:16px;grid-column:1/-1">
          <div class="spinner" style="margin:0 auto 16px"></div>
          Loading orders…
        </div>
      </div>
    </div>
  `;

  document.getElementById('back-btn')!.addEventListener('click', () => {
    if (pollInterval) { clearInterval(pollInterval); pollInterval = null; }
    navigate('restaurant');
  });

  document.getElementById('history-btn')!.addEventListener('click', () => navigate('restaurant-orders'));

  await loadOrders(session?.businessId ?? 'preview');

  pollInterval = setInterval(() => loadOrders(session?.businessId ?? 'preview'), 8000);

  // Immediate refresh when tablet sends a new order (same-window event)
  const onNewOrder = () => loadOrders(session?.businessId ?? 'preview');
  window.addEventListener('kitchen:new-order', onNewOrder);
  // Clean up listener when navigating away (next poll clear also removes it)
  const origClear = () => { window.removeEventListener('kitchen:new-order', onNewOrder); };
  document.getElementById('back-btn')!.addEventListener('click', origClear, { once: true });
}

async function loadOrders(businessId: string): Promise<void> {
  let orders: Order[] = [];
  try {
    const api = getAPI();
    orders = await api.getOrders(businessId, true) as Order[];
  } catch {
    orders = getPreviewOrderStore(businessId);
  }

  const el = document.getElementById('last-updated');
  if (el) el.textContent = `Last updated: ${new Date().toLocaleTimeString()}`;
  renderKitchenBoard(orders, businessId);
}

function renderKitchenBoard(orders: Order[], businessId: string): void {
  const board = document.getElementById('kitchen-board')!;

  const active = orders.filter((o) => !['delivered', 'cancelled'].includes(o.status));

  if (active.length === 0) {
    board.innerHTML = `
      <div style="color:rgba(255,255,255,.4);text-align:center;padding:80px;font-size:16px;grid-column:1/-1">
        <div style="font-size:48px;margin-bottom:16px">✓</div>
        All caught up — no active orders
      </div>
    `;
    return;
  }

  const statusColor: Record<string, string> = {
    pending: '#f59e0b',
    preparing: '#3b82f6',
    ready: '#10b981',
  };

  board.innerHTML = active.map((order) => {
    const age = Math.floor((Date.now() - new Date(order.createdAt).getTime()) / 60000);
    const isUrgent = age >= 15 && order.status === 'pending';

    return `
      <div class="kitchen-ticket ${isUrgent ? 'urgent' : ''}" data-id="${order.id}" style="border-top:4px solid ${statusColor[order.status] ?? '#64748b'}">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:12px">
          <div style="font-size:24px;font-weight:900">Table ${escHtml(order.tableNumber)}</div>
          <div style="text-align:right">
            <div style="font-size:10px;opacity:.7">${age}m ago</div>
            <div style="font-size:11px;font-weight:700;color:${statusColor[order.status] ?? '#64748b'};text-transform:uppercase">${order.status}</div>
          </div>
        </div>

        <div style="display:flex;flex-direction:column;gap:6px;margin-bottom:14px">
          ${order.items.map((item) => `
            <div style="display:flex;justify-content:space-between;align-items:center">
              <span style="font-size:15px"><strong>${item.quantity}×</strong> ${escHtml(item.name)}</span>
              ${item.notes ? `<span style="font-size:10px;color:#f59e0b;background:rgba(245,158,11,.15);padding:2px 6px;border-radius:4px">${escHtml(item.notes)}</span>` : ''}
            </div>
          `).join('')}
        </div>

        ${order.notes ? `<div style="font-size:11px;color:#f59e0b;border-top:1px solid rgba(255,255,255,.1);padding-top:8px;margin-bottom:10px">Note: ${escHtml(order.notes)}</div>` : ''}

        <div style="display:flex;gap:6px">
          ${order.status === 'pending' ? `
            <button class="kitchen-btn kitchen-btn-blue" data-action="preparing" data-id="${order.id}">Start Preparing</button>
          ` : order.status === 'preparing' ? `
            <button class="kitchen-btn kitchen-btn-green" data-action="ready" data-id="${order.id}">Mark Ready 🔔</button>
          ` : `
            <button class="kitchen-btn kitchen-btn-muted" data-action="delivered" data-id="${order.id}">Mark Delivered</button>
          `}
          <button class="kitchen-btn kitchen-btn-danger" data-action="cancelled" data-id="${order.id}">Cancel</button>
        </div>
      </div>
    `;
  }).join('');

  board.querySelectorAll('[data-action]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const action = btn.getAttribute('data-action') as Order['status'];
      const id = btn.getAttribute('data-id')!;
      try {
        await getAPI().updateOrderStatus(id, action);
      } catch {
        previewUpdateOrderStatus(id, action);
      }
      window.dispatchEvent(new CustomEvent('kitchen:order-updated'));
      await loadOrders(businessId);
    });
  });
}

// ── Shared in-memory preview store (window.__preuve_orders) ──────────────────
// RestaurantTablet.ts writes to this same key when the real API is unavailable.

type PreviewStore = { orders: Order[] };

export function getPreviewOrderStore(businessId: string): Order[] {
  const w = window as unknown as { __preuve_orders?: PreviewStore };
  if (!w.__preuve_orders) {
    const now = new Date();
    w.__preuve_orders = {
      orders: [
        {
          id: 'o1', businessId, tableNumber: '4', status: 'pending', total: 47.97,
          createdAt: new Date(now.getTime() - 5 * 60000).toISOString(), updatedAt: now.toISOString(),
          items: [
            { menuItemId: '1', name: 'Classic Burger', quantity: 2, price: 14.99 },
            { menuItemId: '7', name: 'Craft Lemonade', quantity: 2, price: 4.99 },
          ],
        },
        {
          id: 'o2', businessId, tableNumber: '7', status: 'preparing', total: 43.98,
          createdAt: new Date(now.getTime() - 12 * 60000).toISOString(), updatedAt: now.toISOString(),
          items: [
            { menuItemId: '4', name: 'Grilled Salmon', quantity: 1, price: 24.99 },
            { menuItemId: '5', name: 'Chicken Alfredo', quantity: 1, price: 18.99 },
          ],
        },
        {
          id: 'o3', businessId, tableNumber: '2', status: 'ready', total: 20.98,
          createdAt: new Date(now.getTime() - 18 * 60000).toISOString(), updatedAt: now.toISOString(),
          items: [
            { menuItemId: '6', name: 'Mozzarella Sticks', quantity: 1, price: 8.99 },
            { menuItemId: '3', name: 'Caesar Salad', quantity: 1, price: 11.99 },
          ],
          notes: 'Dressing on the side',
        },
      ],
    };
  }
  return w.__preuve_orders.orders;
}

function previewUpdateOrderStatus(id: string, status: Order['status']): void {
  const w = window as unknown as { __preuve_orders?: PreviewStore };
  const order = w.__preuve_orders?.orders.find((o) => o.id === id);
  if (order) { order.status = status; order.updatedAt = new Date().toISOString(); }
}

// Called by RestaurantTablet when the real API is unavailable
export function previewAddOrder(order: Order): void {
  const w = window as unknown as { __preuve_orders?: PreviewStore };
  if (!w.__preuve_orders) { getPreviewOrderStore(order.businessId); }
  w.__preuve_orders!.orders.unshift(order);
}

function getAPI() {
  return (window as unknown as { preuve: Record<string, (...args: unknown[]) => Promise<unknown>> }).preuve;
}
function escHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
